import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CssBaseline,
  Paper,
  Typography
} from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { useLocation } from 'react-router-dom';
import anearApi from '../Api/AnearApi';
import UserAuthContainer from '../Containers/UserAuthContainer';
import useNavigation from '../Utils/navigationUtils';
import { CONTENT_COLUMN_MAX_WIDTH, contentColumnPageShellSx } from '../UI/contentColumn'
import PageBackRow from '../UI/PageBackRow'

const VerifyEmailPage = () => {
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const hasAttemptedVerification = useRef(false);
  const [verifying, setVerifying] = useState(Boolean(token));
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(token ? null : new Error('Email verification link is missing or invalid.'));
  const userAuthContainer = UserAuthContainer.useContainer();
  const { navigateToLogin, navigateToPathWithReplace, resolveHomePath } = useNavigation();

  useEffect(() => {
    if (!token || hasAttemptedVerification.current) return;
    hasAttemptedVerification.current = true;

    const runVerification = async () => {
      try {
        const user = await anearApi.verifyEmail(token);
        await userAuthContainer.setUser(user);
        setVerified(true);
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setVerifying(false);
      }
    };

    runVerification();
  }, [token, userAuthContainer]);

  const continueHandler = () => {
    navigateToPathWithReplace(resolveHomePath());
  };

  return (
    <Box sx={contentColumnPageShellSx}>
      <CssBaseline />
      <Paper
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          px: 3,
          pb: 3,
          bgcolor: '#0B0F19',
          color: '#E6E9EF',
          borderRadius: 3,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: CONTENT_COLUMN_MAX_WIDTH
        }}
      >
        {!verified && (
          <PageBackRow onClick={navigateToLogin} label="Back to login" sx={{ mb: 1 }} />
        )}
        <Avatar sx={{ m: 1, backgroundColor: 'rgba(124, 58, 237, 0.3)', color: '#A78BFA' }}>
          <MarkEmailReadIcon />
        </Avatar>
        <Typography component="h1" variant="h5" sx={{ color: '#E6E9EF', fontWeight: 800, letterSpacing: 0.2 }}>
          Verify email
        </Typography>

        {verifying && (
          <Typography
            variant="body1"
            sx={{ mt: 2, color: 'rgba(230,233,239,0.82)', textAlign: 'center', maxWidth: '30rem' }}
          >
            Verifying your email now...
          </Typography>
        )}

        {verified && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              width: '100%',
              bgcolor: 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.25)'
            }}
          >
            <Typography variant="body1" sx={{ color: '#D1FAE5', fontWeight: 600, textAlign: 'center' }}>
              Your email is verified and you are now signed in.
            </Typography>
          </Box>
        )}

        {!verifying && error && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              width: '100%',
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)'
            }}
          >
            <Typography variant="body2" sx={{ color: '#EF4444', fontWeight: 500, lineHeight: 1.5, textAlign: 'center' }}>
              {error.message || 'Unable to verify your email.'}
            </Typography>
          </Box>
        )}

        {verified && (
          <Button
            sx={{
              mt: 2,
              color: '#A78BFA',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'rgba(167,139,250,0.1)'
              }
            }}
            size="small"
            onClick={continueHandler}
          >
            Continue
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default VerifyEmailPage;
